import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Section components
import CallToAction from '../../components/sections/CallToAction';

// UI Components
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

// Data
import { caseStudies } from '../../data/mockData';

/**
 * Case Study Detail page component with dynamic routing
 */
const CaseStudyDetail: React.FC = () => {
  // Get the case study slug from URL params
  const { slug } = useParams();

  // Find the case study by matching the URL path with the study URL
  const caseStudy = caseStudies.find(study => {
    // Extract slug from full URL (e.g., '/case-studies/retail-case' → 'retail-case')
    const studySlug = study.url.split('/').pop();
    return studySlug === slug;
  });

  // If case study not found, redirect to the case studies listing page
  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  // Find related case studies with the same industry or solution
  const relatedCaseStudies = caseStudies
    .filter(study => 
      study.id !== caseStudy.id && 
      (study.industry === caseStudy.industry || study.solution === caseStudy.solution)
    )
    .slice(0, 2); // Limit to 2 related case studies

  return (
    <Layout>
      {/* Hero Section */}
      <Section bgColor="bg-gradient-to-br from-primary to-secondary" padding="lg" className="text-white">
        <Container>
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <svg className="mr-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to all case studies
            </Link>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 mb-4">
                {caseStudy.industry} • {caseStudy.solution}
              </span>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{caseStudy.title}</h1>
              
              <p className="text-xl text-white/90">{caseStudy.excerpt}</p>
            </motion.div>
          </div>
        </Container>
      </Section>
      
      {/* Key Results Section */}
      <Section bgColor="bg-white" padding="md">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6 md:p-8">
              <h2 className="text-2xl font-bold mb-6 text-center">Key Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {caseStudy.results.map((result, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="bg-white rounded-lg p-6 shadow-sm h-full flex flex-col items-center justify-center">
                      <div className="text-primary text-2xl md:text-3xl font-bold mb-2">
                        {result.split(' ')[0]}
                      </div>
                      <div className="text-neutral-700">
                        {result.split(' ').slice(1).join(' ')}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Main Content */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <h2>Challenge</h2>
              <p>
                {caseStudy.industry} companies face increasing pressure to leverage their data effectively 
                while managing costs and maintaining security. Our client was struggling with {caseStudy.solution.toLowerCase()} 
                that couldn't scale with their growing business needs.
              </p>
              
              <p>
                They needed a solution that could:
              </p>
              
              <ul>
                <li>Integrate with their existing systems without disruption</li>
                <li>Provide actionable insights from their customer data</li>
                <li>Automate key processes to reduce operational costs</li>
                <li>Scale with their business growth</li>
              </ul>
              
              <h2>Our Approach</h2>
              <p>
                After a thorough assessment of their existing systems and processes, we developed a 
                customized implementation plan focused on achieving their specific business objectives.
              </p>
              
              <p>
                Our team worked closely with stakeholders across the organization to ensure the solution 
                would meet the needs of all departments while providing a seamless transition.
              </p>
              
              <h3>Phase 1: Assessment & Strategy</h3>
              <p>
                We began with a comprehensive analysis of their current workflows, identifying 
                bottlenecks and opportunities for AI-driven improvements.
              </p>
              
              <h3>Phase 2: Solution Design</h3>
              <p>
                Based on our findings, we designed a custom {caseStudy.solution.toLowerCase()} solution 
                that addressed their specific challenges while leveraging their existing infrastructure.
              </p>
              
              <h3>Phase 3: Implementation & Training</h3>
              <p>
                Our team handled the technical implementation while providing comprehensive training 
                to ensure high adoption rates across the organization.
              </p>
              
              <h2>Results</h2>
              <p>
                The implementation delivered significant improvements across multiple areas of the business:
              </p>
              
              <ul>
                {caseStudy.results.map((result, index) => (
                  <li key={index}><strong>{result}</strong> - driving substantial business impact.</li>
                ))}
              </ul>
              
              <blockquote>
                "The solution provided by CodemyAI has transformed how we operate. The results 
                exceeded our expectations, and the ROI has been remarkable."
                <footer>- Client Executive Sponsor</footer>
              </blockquote>
              
              <h2>Long-Term Impact</h2>
              <p>
                Beyond the immediate results, our solution has positioned the organization for 
                continued success with AI-driven insights and processes. They now have a 
                scalable foundation for future innovation.
              </p>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Related Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <Section bgColor="bg-neutral-50" padding="lg">
          <Container>
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">Related Case Studies</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedCaseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={study.url} className="block h-full">
                    <Card hover className="h-full">
                      <div className="aspect-video bg-neutral-200 rounded-lg mb-4 overflow-hidden">
                        <div className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center">
                          <span className="text-white text-lg font-semibold">{study.industry}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {study.solution}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                      <p className="text-neutral-600 mb-4">{study.excerpt}</p>
                      
                      <div className="mt-auto pt-2">
                        <span className="inline-flex items-center text-primary font-medium">
                          Read full case study
                          <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </Container>
        </Section>
      )}
      
      {/* CTA Section */}
      <CallToAction
        title="Ready to achieve similar results?"
        description={`Schedule a consultation to discuss how our ${caseStudy.solution} solutions can transform your business.`}
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default CaseStudyDetail;